import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Munkaidő (worktime) — DTO-k a 23-logic.js wtEntries/wtSettings/worktimeVals
// state-jéből levezetve. A backend a CONTRACTS.md szerint implementálja.
// ============================================================================

/** 'absence' = Távollét (nem fizetett; idősáv opcionális, üres = egész nap). */
export type WorktimeEntryType = 'work' | 'vacation' | 'sick' | 'absence';

export interface WorktimeEntry {
  id: number;
  userId: number;
  /** 'YYYY-MM-DD' */
  date: string;
  type: WorktimeEntryType;
  /** 'HH:mm' — type='work' (kötelező) és 'absence' (opcionális) esetén */
  startTime: string | null;
  /** 'HH:mm' — null = a munkanap folyamatban van */
  endTime: string | null;
  comment: string;
  /** Szerver számolja: (end - start) - 0.5 óra ebédszünet; null ha nincs end */
  workedHours: number | null;
}

export interface SaveWorktimeEntryRequest {
  /** Admin megadhatja; nélküle a bejelentkezett userre vonatkozik */
  userId?: number;
  date: string;
  type: WorktimeEntryType;
  startTime?: string | null;
  endTime?: string | null;
  comment?: string;
}

export interface WorktimeConfig {
  userId: number;
  /** Kötelező napi óraszám (óra/nap) — wtSettings.daily */
  dailyRequiredHours: number;
  /** Éves szabadság (nap/év) — wtSettings.vacation */
  annualVacationDays: number;
  /** Rendszeren kívül már felhasznált szabadnapok — wtSettings.usedPrior */
  usedPriorVacationDays: number;
  /** Órabér (Ft/óra) — wtSettings.wage */
  hourlyWage: number;
  /** Szabadnap elszámolása (óra bér / szabadnap) — wtSettings.vacDayHours */
  vacationDayHours: number;
}

export type UpdateWorktimeConfigRequest = Partial<Omit<WorktimeConfig, 'userId'>> & {
  userId?: number;
};

export type EmploymentType = 'employee' | 'external';

export interface WorktimeSummary {
  userId: number;
  /** Külsősnél nincs szabadság- és betegszabadság-elszámolás (a vonatkozó mezők 0-k). */
  employmentType: EmploymentType;
  year: number;
  /** 1-12 */
  month: number;
  workedHours: number;
  /** munkanapok száma × napi kötelező óra */
  requiredHours: number;
  workdayCount: number;
  /** napok, amikre munkanap van rögzítve */
  daysWorked: number;
  /** lezáratlan (vége nélküli) munkanapok a mai nap előtt — 0 órával számítanak */
  openDays: number;
  /** egész napos távollétek (bejegyzés + terv) */
  absenceDaysInMonth: number;
  /** távollét órák összesen (egész nap = napi kötelező óra) */
  absenceHoursInMonth: number;
  vacationDaysInMonth: number;
  sickDaysInMonth: number;
  /** idei bejegyzett szabadságok + usedPriorVacationDays */
  vacationDaysUsedThisYear: number;
  vacationDaysTotal: number;
  vacationDaysLeft: number;
  /** workedHours×wage + vacDays×vacationDayHours×wage + sickDays×daily×wage×0.7 */
  expectedGrossSalary: number;
}

/** Előre jelzett távollét — a nap tényleges bejegyzése felülírja. */
export interface WorktimePlan {
  id: number;
  userId: number;
  /** null = egyszeri; 1..7 = hetente (1 = hétfő) */
  weekday: number | null;
  dateFrom: string;
  dateTo: string | null;
  /** mindkettő null = egész nap */
  startTime: string | null;
  endTime: string | null;
  comment: string;
}

export interface SaveWorktimePlanRequest {
  userId?: number;
  weekday?: number | null;
  dateFrom: string;
  dateTo?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  comment?: string;
}

/** Egy nap tervezett távolléte (csak ahol nincs aznapi bejegyzés). */
export interface WorktimePlannedDay {
  date: string;
  planId: number;
  startTime: string | null;
  endTime: string | null;
  comment: string;
}

export interface WorktimeOverviewRow extends WorktimeSummary {
  name: string;
  hourlyWage: number;
}

@Injectable({ providedIn: 'root' })
export class WorktimeService {

  constructor(private http: HttpClient) {}

  /** GET worktime/entries?year=&month=&userId= — a hónap bejegyzései */
  getEntries(year: number, month: number, userId?: number): Observable<WorktimeEntry[]> {
    let params = new HttpParams().set('year', year).set('month', month);
    if (userId != null) params = params.set('userId', userId);
    return this.http.get<WorktimeEntry[]>('worktime/entries', { params });
  }

  /** POST worktime/entries — upsert (user + dátum szerint) */
  saveEntry(request: SaveWorktimeEntryRequest): Observable<WorktimeEntry> {
    return this.http.post<WorktimeEntry>('worktime/entries', request);
  }

  /** DELETE worktime/entries/:date?userId= — nap bejegyzés törlése */
  deleteEntry(date: string, userId?: number): Observable<void> {
    let params = new HttpParams();
    if (userId != null) params = params.set('userId', userId);
    return this.http.delete<void>(`worktime/entries/${date}`, { params });
  }

  /** GET worktime/config?userId= — admin konfig (userId nélkül a sajátom) */
  getConfig(userId?: number): Observable<WorktimeConfig> {
    let params = new HttpParams();
    if (userId != null) params = params.set('userId', userId);
    return this.http.get<WorktimeConfig>('worktime/config', { params });
  }

  /** PATCH worktime/config — admin konfig módosítás */
  updateConfig(request: UpdateWorktimeConfigRequest): Observable<WorktimeConfig> {
    return this.http.patch<WorktimeConfig>('worktime/config', request);
  }

  /** GET worktime/summary?year=&month=&userId= — havi összesítő */
  getSummary(year: number, month: number, userId?: number): Observable<WorktimeSummary> {
    let params = new HttpParams().set('year', year).set('month', month);
    if (userId != null) params = params.set('userId', userId);
    return this.http.get<WorktimeSummary>('worktime/summary', { params });
  }

  /** GET worktime/overview?year=&month= — admin: mindenki havi összesítője */
  getOverview(year: number, month: number): Observable<WorktimeOverviewRow[]> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.http.get<WorktimeOverviewRow[]>('worktime/overview', { params });
  }

  /** GET worktime/plans?userId= — aktív (le nem járt) tervezett távollétek */
  getPlans(userId?: number): Observable<WorktimePlan[]> {
    let params = new HttpParams();
    if (userId != null) params = params.set('userId', userId);
    return this.http.get<WorktimePlan[]>('worktime/plans', { params });
  }

  /** GET worktime/planned-days?year=&month=&userId= — a tervek kibontva a hónapra */
  getPlannedDays(year: number, month: number, userId?: number): Observable<WorktimePlannedDay[]> {
    let params = new HttpParams().set('year', year).set('month', month);
    if (userId != null) params = params.set('userId', userId);
    return this.http.get<WorktimePlannedDay[]>('worktime/planned-days', { params });
  }

  savePlan(request: SaveWorktimePlanRequest): Observable<WorktimePlan> {
    return this.http.post<WorktimePlan>('worktime/plans', request);
  }

  deletePlan(id: number, userId?: number): Observable<void> {
    let params = new HttpParams();
    if (userId != null) params = params.set('userId', userId);
    return this.http.delete<void>(`worktime/plans/${id}`, { params });
  }
}
